import * as React from "react";
import clsx from "clsx";
import { StepIndicator } from "@/components/ui/StepIndicator";
import { Button, message } from "antd";
import { CSSTransition } from "react-transition-group";
import { formatTemplateLabels, formatTemplateStepSize } from "@/utils/alphaAgent/helper";
import { useFormatTemplateStore } from "@/state/formatTemplateStore";
import CreateNewStep from "@/formatTemplate/components/CreateNewStep";
import PPTSlidesPreview from "@/formatTemplate/components/PPTSlidesPreview";
import { handleUpload } from "@/utils/formatTemplate/slides-extraction-handlers";
import FontsStep from "@/formatTemplate/components/FontsStep";
import ColorsStep from "@/formatTemplate/components/ColorsStep";
import NumericalSelectionStep from "@/formatTemplate/components/NumericalSelectionStep";
import EditApprovalSection from "@/formatTemplate/components/EditApprovalSection";
import { useEffect } from "react";
import { ulid } from "ulidx";
import { createFormatTemplate } from "@/utils/formatTemplate/requestHelper";

function Step({ show, children }) {
  const nodeRef = React.useRef<HTMLDivElement | null>(null);
  return (
    <CSSTransition
      in={show} // ← visible or hidden?
      timeout={150}
      classNames="step" // ← base for all generated class-names
      mountOnEnter={true} // keep it in the DOM from the first render
      unmountOnExit={true} // never un-mount (so state survives)
      appear={show}
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="step-slot">{children}</div>
    </CSSTransition>
  );
}

export default function CreateFormatTemplate() {
  const {
    questionsPerStep,
    currentStep,
    setCurrentStep,
    setDisabledButton,
    setIsloading,
    nextStep,
    setFormatId,
    setFormatTemplateSidebar,
    resetStore,
    setIsAllowedToEdit,
  } = useFormatTemplateStore();
  const disabledButton = useFormatTemplateStore((state) => state.disabledButton);
  const answersByStep = useFormatTemplateStore((state) => state.answersByStep);

  console.log("CreateFormatTemplate rendered", { currentStep, disabledButton });
  const handleNextClick = React.useCallback(async () => {
    if (currentStep === 5) {
      const {
        formatId,
        formatTemplateData,
        imagesWithCategory,
        fontsSection,
        colorsSection,
        numericalSelection,
        editApprovals,
      } = useFormatTemplateStore.getState();
      const data = {
        formatName: `${formatTemplateData.companyName.trim()}-${formatTemplateData.templateName.trim()}`,
        formatType: "EXCEL",
        formatData: JSON.stringify({
          fontsSection,
          colorsSection,
          numericalSelection,
        }),
        imagesWithCategory,
        editApprovals,
        update: true,
      };
      try {
        const response = await createFormatTemplate(formatId, data);
        if (response && response.success) {
          message.success("Format template created successfully!");
          setFormatTemplateSidebar("search-existing");
          const { resetStore } = useFormatTemplateStore.getState();
          resetStore();
        } else {
          message.error("Failed to create format template. Please try again.");
        }
      } catch (e: any) {
        message.error(e.response.data.message || "Failed to create format template. Please try again.");
      }
      return;
    }
    nextStep();
    const isFileChanged = localStorage.getItem("isFileChanged") === "true";
    if (currentStep === 0 && isFileChanged) {
      setIsloading(true);
      const { updateAnswered } = useFormatTemplateStore.getState();
      updateAnswered(1, 0);
      const { file } = useFormatTemplateStore.getState().formatTemplateData;
      const formatId = useFormatTemplateStore.getState().formatId;
      await handleUpload(formatId, file);
      localStorage.removeItem("isFileChanged");
    }
  }, [currentStep, setCurrentStep, setDisabledButton, setFormatId]);

  useEffect(() => {
    const id = ulid();
    setIsAllowedToEdit(true);
    const isUpdate = localStorage.getItem("isUpdate");
    if (isUpdate !== "true") {
      resetStore();
      setFormatId(id);
    }
    localStorage.removeItem("isUpdate");
  }, []);

  return (
    <div className="flex flex-col justify-between h-full min-h-screen grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4 bg-gray-100">
      <div
        className={clsx(
          "w-full mx-auto py-4 shadow-md px-4 rounded-sm z-10 border border-gray-200 border-b-[2px] border-b-[#ff9336] bg-white"
        )}
      >
        <StepIndicator
          currentStep={currentStep}
          answersByStep={answersByStep}
          questionsPerStep={questionsPerStep}
          labels={formatTemplateLabels}
          stepSizes={formatTemplateStepSize}
        />
      </div>

      <div className={"grid h-full relative w-full "}>
        <Step show={currentStep === 0}>
          <CreateNewStep />
        </Step>
        <Step show={currentStep === 1}>
          <PPTSlidesPreview />
        </Step>
        <Step show={currentStep === 2}>
          <FontsStep />
        </Step>
        <Step show={currentStep === 3}>
          <ColorsStep />
        </Step>
        <Step show={currentStep === 4}>
          <NumericalSelectionStep />
        </Step>
        <Step show={currentStep === 5}>
          <EditApprovalSection />
        </Step>
      </div>
      <div className="flex justify-end w-full px-2 pt-4  gap-2">
        <Button danger onClick={() => Office.context.ui.messageParent("CLOSE")}>
          Cancel
        </Button>
        {currentStep !== 0 && (
          <Button
            type="default"
            onClick={() => {
              const { prevStep } = useFormatTemplateStore.getState();
              prevStep();
            }}
          >
            Back
          </Button>
        )}

        <Button type={"primary"} disabled={disabledButton} className={""} onClick={handleNextClick}>
          {currentStep >= 5 ? "Create" : "Next"}
        </Button>
      </div>
    </div>
  );
}
